"use client";

import React from "react";

/**
 * Defers mounting `children` until the wrapper scrolls within `rootMargin` of
 * the viewport (IntersectionObserver), then mounts once and disconnects.
 *
 * Purpose: keep embla-carousel's init-time layout measurement (a forced reflow)
 * off the initial-hydration critical path. Embla measures the container + every
 * slide rect inside `createEngine()` the moment `<Carousel>` mounts — even when
 * the carousel is below the fold or `display:none`. Gating the mount moves that
 * work to scroll-approach instead of first paint. (Embla's own `active:false`
 * does NOT help: `activate()` calls `createEngine()` before it bails on
 * inactive, so the measurement still runs.)
 *
 * Reserve space with `style={{ minHeight }}` so the off-screen placeholder→
 * content swap doesn't collapse scroll height and jolt a fast scroll.
 *
 * SSR renders nothing inside (initial `show` is false), so only place
 * client-only / non-SEO-critical content here. (e.g. the mobile-only card
 * carousel, whose SEO-visible sibling is the server-rendered desktop grid.)
 */
export default function LazyMount({
  children,
  rootMargin = "600px",
  className,
  style,
}: {
  children: React.ReactNode;
  rootMargin?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (very old browser / non-DOM env): mount eagerly so
    // content is never permanently hidden. One-shot fallback, not a render loop.
    if (typeof IntersectionObserver === "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShow(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} className={className} style={style}>
      {show ? children : null}
    </div>
  );
}
